import { track } from '@amplitude/analytics-browser';
import { CommonModule } from '@angular/common';
import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetModule, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { loadStripe, PaymentIntentResult, PaymentRequest, SetupIntentResult, Stripe, StripeElements, StripeError } from '@stripe/stripe-js';
import { combineLatest, from, fromEvent, map, switchMap, take, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiService } from '../../services/api.service';
import { LoadingService } from '../../services/loading.service';
import { ProductView } from '../checkout/checkout.component';
import { ModalComponent, ModalData } from '../modal/modal.component';

export interface PaymentData {
  product: ProductView;
  buttonTitle: string;
  email?: string;
  name?: string;
  stripeCustomerId?: string;
  isDisclaimerShown: boolean;
  insufficientFundsAction?: () => void;
}

@Component({
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule, MatButtonModule, MatDialogModule, MatBottomSheetModule],
  selector: 'app-payment',
  templateUrl: './payment.component.html',
  styleUrl: './payment.component.scss'
})
export class PaymentComponent implements OnInit {
  @ViewChild('expressCheckout') expressCheckoutContainer!: ElementRef;
  @ViewChild('stripeElement') stripeElement!: ElementRef;
  @ViewChild('stripeForm') stripeForm!: ElementRef;
  @ViewChild('stripeEmail') stripeEmail!: ElementRef;

  isLoading: boolean = true;

  email: string | undefined;
  name: string | undefined;
  product: ProductView;

  isExpressMethodsAvailable: boolean = true;
  purchaseButtonTitle = 'Continue';

  isDisclaimerShown: boolean;

  private stripeCustomerId: string | undefined;
  private clientSecret: string;

  private stripeElements: StripeElements | undefined;
  private stripeLoaded$ = from(loadStripe(environment.stripePubKey));

  private insufficientFundsAction: (() => void) | undefined;

  constructor(
    private router: Router,
    private api: ApiService,
    private loadingService: LoadingService,
    private dialog: MatDialog,
    private bottomSheetRef: MatBottomSheetRef<PaymentComponent, boolean>,
    @Inject(MAT_BOTTOM_SHEET_DATA) data: PaymentData,
    ) {
      this.email = data.email;
      this.product = data.product;
      this.purchaseButtonTitle= data.buttonTitle;
      this.name = data.name;
      this.stripeCustomerId = data.stripeCustomerId;
      this.insufficientFundsAction = data.insufficientFundsAction;
      this.isDisclaimerShown = data.isDisclaimerShown;
    }

  ngOnInit(): void {
    const subscription$ = this.api.createSubscription(this.product.id, this.stripeCustomerId, this.email).pipe(
      tap(({ clientSecret }) => {
        this.clientSecret = clientSecret;
      }),
    );

    combineLatest([subscription$, this.stripeLoaded$])
      .pipe(
        take(1),
        map(([_, stripe]) => stripe),
        tap((stripe) => from(this.createCardForm(stripe!))),
        switchMap((stripe) => from(this.createQuickMethodForm(stripe!))),
        tap(() => this.isLoading = false),
        switchMap((stripe) => this.watchStripe(stripe)),
      )
      .subscribe();
  }

  close(isSuccess: boolean) {
    this.bottomSheetRef.dismiss(isSuccess);
  }

  private async createCardForm(stripe: Stripe) {
    this.stripeElements = stripe.elements({
      clientSecret: this.clientSecret,
      appearance: {
        theme: 'stripe',
        variables: {
          colorBackground: '#4E4238',
          colorText: '#ffffff',
          fontFamily: `'Nunito Sans', sans-serif`,
          colorPrimary: '#FDF3EC8C',
          colorTextPlaceholder: '#FDF3EC8C'
        }
      },
      loader: 'always'
    });

    this.stripeElements?.create('payment', {
      paymentMethodOrder: ['card', 'paypal'],
      wallets: {
        applePay: 'never',
        googlePay: 'never',
      },
      layout: 'auto',
    }).mount(this.stripeElement.nativeElement);

    if (!this.email) {
      this.stripeElements?.create('linkAuthentication').mount(this.stripeEmail.nativeElement);
    }
  }

  private async createQuickMethodForm(stripe: Stripe) {
    const paymentRequest = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label: 'Total',
        amount: Math.round(100 * (this.product.setupFeePriceUsd || this.product.introductoryPriceUsd || this.product.recurringPriceUsd)),
      },
      requestShipping: false,
      requestPayerName: false, //TODO: maybe make it true
      requestPayerEmail: !this.email,
    });
    const applePayElement = this.stripeElements?.create('paymentRequestButton', {
      paymentRequest,
      style: {
        paymentRequestButton: {
          //type: 'subscribe',
          height: '48px',
          buttonSpacing: '8px',
        }
      }
    });

    const result = await paymentRequest.canMakePayment();

    if (result) {
      applePayElement?.mount(this.expressCheckoutContainer.nativeElement);
      this.trackApplePay(stripe, paymentRequest);
    } else {
      this.isExpressMethodsAvailable = false;
    }

    return stripe;
  }

  private trackApplePay(stripe: Stripe, paymentRequest: PaymentRequest) {
    paymentRequest.on('paymentmethod', async (ev) => {
      const { paymentIntent, error: confirmError } = await stripe.confirmCardPayment(
        this.clientSecret,
        { payment_method: ev.paymentMethod.id },
        { handleActions: false }
      );
    
      if (confirmError) {
        ev.complete('fail');
        this.handleError(confirmError);
      } else {
        ev.complete('success');

        if (paymentIntent.status === "requires_action") {
          const { error } = await stripe.confirmCardPayment(this.clientSecret);

          if (error) {
            this.handleError(error);
          } else {
            this.goToSuccess();
          }
        } else {
          this.goToSuccess();
        }
      }
    });
  }

  private watchStripe(stripe: Stripe) {
    const confirmParams = {
      elements: this.stripeElements!,
      confirmParams: {
        return_url: `${location.origin}/result`
      },
      redirect: 'if_required' as 'if_required'
    };

    return fromEvent(this.stripeForm.nativeElement, 'submit')
      .pipe(
        tap((event: any) => event.preventDefault()),
        tap(() => this.loadingService.start()),
        switchMap(() => from(stripe.confirmPayment(confirmParams))),
        tap((result: PaymentIntentResult) => {
          this.loadingService.finish();

          if (result.error) {
            return this.handleError(result.error);
          }
  
          console.log(result.paymentIntent);
    
          this.goToSuccess();
        }),
      );
  }

  private goToSuccess() {
    this.loadingService.finish();
    this.close(true);
    this.router.navigate(['result']);
  }

  private handleError(error: StripeError) {  
    console.log(error);

    this.loadingService.finish();

    if (error.type === 'validation_error') {
      return;
    }

    track('web_subscribe_failure', {
      error: error.code || error.message
    });

    if (error.decline_code === 'insufficient_funds' && this.insufficientFundsAction) {
      this.close(true); //attention here
      this.insufficientFundsAction();
      return;
    }

    this.stripeElements?.getElement('card')?.clear();

    //TODO: in case of insufficient funds propose discount
    this.dialog.open<ModalComponent, ModalData>(ModalComponent, {
      data: {
        title: 'Sorry',
        message: error.message || 'Your payment failed. Please Try Again.',
        rive: {
          fileName: 'credit_card',
          animations: ['Timeline 1'],
          ratio: 1.1 / 1,
        },
      }
    })
      .afterClosed()
      .subscribe();
  }
}
